import { editorUrl } from '@/lib/editorBasePath';
import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, QrCode, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';


interface QrCodeItem {
  id: number;
  url: string;
  image_url: string;
}

interface QrCodePanelProps {
  onAddQrCode: (imageUrl: string, width: number, height: number, qrcodeId: number) => void;
  initialFavoritesColors: string[];
}

const QrCodePanel: React.FC<QrCodePanelProps> = ({ onAddQrCode, initialFavoritesColors }) => {
  const [qrcodes, setQrcodes] = useState<QrCodeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleDelete = (qrCodeId: number) => {
    setDeletingId(qrCodeId);
    fetch(editorUrl(`/remove_qrcode?qr_code_id=${qrCodeId}`), { method: 'DELETE' })
      .then(() => {
        setQrcodes((prev) => prev.filter((qr) => qr.id !== qrCodeId));
      })
      .catch(() => {})
      .finally(() => setDeletingId(null));
  };

  const loadQrCodes = useCallback(() => {
    setLoading(true);
    fetch(editorUrl('/load_qrcodes'))
      .then((res) => res.json())
      .then((data: QrCodeItem[]) => {
        setQrcodes(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadQrCodes();
  }, [loadQrCodes]);

  const handleSave = () => {
    if (!url.trim()) return;
    setSaving(true);
    fetch(editorUrl('/save_qrcode'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: url.trim(), colors: selectedColors }),
    })
      .then((res) => res.json())
      .then(() => {
        setDialogOpen(false);
        setUrl('');
        setSelectedColors([]);
        loadQrCodes();
      })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const toggleColor = (color: string) => {
    setSelectedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-editor-sidebar-foreground text-center flex items-center justify-center gap-1.5">
        <QrCode size={15} />
        QR Code
      </h3>

      <Button
        size="sm"
        className="w-full bg-primary-gradient text-white hover:opacity-90"
        onClick={() => setDialogOpen(true)}
      >
        <Plus size={14} />
        Ajouter un QR Code
      </Button>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="animate-spin text-muted-foreground" size={20} />
        </div>
      )}

      {!loading && qrcodes.length === 0 && (
        <p className="text-xs text-muted-foreground text-center">Aucun QR Code disponible.</p>
      )}

      {!loading && qrcodes.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {qrcodes.map((qr) => (
            <div key={qr.id} className="relative flex flex-col items-center gap-1 group">
              <button
                onClick={() => onAddQrCode(qr.image_url, 150, 150, qr.id)}
                className="w-full aspect-square rounded-md bg-editor-sidebar-hover border border-editor-sidebar-border hover:scale-105 hover:border-editor-sidebar-active transition-all flex items-center justify-center p-2 overflow-hidden"
                title={qr.url}
              >
                <img
                  src={qr.image_url}
                  alt={qr.url}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </button>
              {/* Delete button */}
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(qr.id); }}
                disabled={deletingId === qr.id}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:scale-110"
                title="Supprimer"
              >
                {deletingId === qr.id ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
              </button>
              <a
                href={qr.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-editor-sidebar-active hover:underline"
              >
                Lien
              </a>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un QR Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="qr-url">Lien</Label>
              <Input
                id="qr-url"
                placeholder="https://exemple.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>

            {initialFavoritesColors.length > 0 && (
              <div className="space-y-2">
                <Label>Couleurs</Label>
                <div className="flex flex-wrap gap-2">
                  {initialFavoritesColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => toggleColor(color)}
                      className="w-7 h-7 rounded-full transition-all"
                      style={{
                        backgroundColor: color,
                        boxShadow: selectedColors.includes(color)
                          ? `0 0 0 2px hsl(var(--background)), 0 0 0 4px ${color}`
                          : 'none',
                        transform: selectedColors.includes(color) ? 'scale(1.1)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={handleSave} disabled={!url.trim() || saving}>
              {saving ? <Loader2 className="animate-spin" size={14} /> : null}
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QrCodePanel;
